import { createRouter, createWebHistory } from 'vue-router'
import type { RouteRecordRaw } from 'vue-router'
import { useAuthStore } from '../stores/auth'
import { useWorkspaceStore } from '../stores/workspace'

// Layouts
const AuthLayout = () => import('../layouts/AuthLayout.vue')
const AppLayout = () => import('../layouts/AppLayout.vue')

// Auth views
const LoginView = () => import('../views/auth/LoginView.vue')
const RegisterView = () => import('../views/auth/RegisterView.vue')
const ForgotPasswordView = () => import('../views/auth/ForgotPasswordView.vue')
const ResetPasswordView = () => import('../views/auth/ResetPasswordView.vue')
const AcceptInvitationView = () => import('../views/auth/AcceptInvitationView.vue')

// App views
const OnboardingView = () => import('../views/OnboardingView.vue')
const DashboardView = () => import('../views/DashboardView.vue')
const WorkspaceView = () => import('../views/WorkspaceView.vue')
const ProjectView = () => import('../views/ProjectView.vue')
const PageView = () => import('../views/PageView.vue')
const SettingsView = () => import('../views/SettingsView.vue')

// Admin views
const AdminDashboardView = () => import('../views/admin/AdminDashboardView.vue')
const AdminUsersView = () => import('../views/admin/AdminUsersView.vue')
const AdminWorkspacesView = () => import('../views/admin/AdminWorkspacesView.vue')

const routes: RouteRecordRaw[] = [
  // Auth routes
  {
    path: '/auth',
    component: AuthLayout,
    children: [
      {
        path: 'login',
        name: 'login',
        component: LoginView,
        meta: { guest: true },
      },
      {
        path: 'register',
        name: 'register',
        component: RegisterView,
        meta: { guest: true },
      },
      {
        path: 'forgot-password',
        name: 'forgot-password',
        component: ForgotPasswordView,
        meta: { guest: true },
      },
      {
        path: 'reset-password',
        name: 'reset-password',
        component: ResetPasswordView,
        meta: { public: true }, // Available to everyone, even authenticated users
      },
    ],
  },

  // Invitation accept route (public but shows different content based on auth status)
  {
    path: '/invite/:token',
    name: 'invite',
    component: AcceptInvitationView,
    meta: { public: true },
  },

  // Onboarding (no sidebar)
  {
    path: '/onboarding',
    name: 'onboarding',
    component: OnboardingView,
    meta: { requiresAuth: true, onboarding: true },
  },

  // App routes (authenticated)
  {
    path: '/',
    component: AppLayout,
    meta: { requiresAuth: true },
    children: [
      {
        path: '',
        name: 'dashboard',
        component: DashboardView,
      },
      {
        path: 'workspace/:workspaceSlug',
        name: 'workspace',
        component: WorkspaceView,
        children: [
          {
            path: 'project/:projectSlug',
            name: 'project',
            component: ProjectView,
          },
          {
            path: 'project/:projectSlug/page/:pageId',
            name: 'page',
            component: PageView,
          },
        ],
      },
      {
        path: 'settings',
        name: 'settings',
        component: SettingsView,
      },
      // Admin routes
      {
        path: 'admin',
        name: 'admin',
        redirect: { name: 'admin-dashboard' },
        meta: { admin: true },
      },
      {
        path: 'admin/dashboard',
        name: 'admin-dashboard',
        component: AdminDashboardView,
        meta: { admin: true },
      },
      {
        path: 'admin/users',
        name: 'admin-users',
        component: AdminUsersView,
        meta: { admin: true },
      },
      {
        path: 'admin/workspaces',
        name: 'admin-workspaces',
        component: AdminWorkspacesView,
        meta: { admin: true },
      },
    ],
  },

  // Redirect to dashboard
  {
    path: '/:pathMatch(.*)*',
    redirect: '/',
  },
]

const router = createRouter({
  history: createWebHistory(),
  routes,
})

// Navigation guards
router.beforeEach(async (to, _from, next) => {
  const authStore = useAuthStore()
  const workspaceStore = useWorkspaceStore()

  // Check route meta
  const requiresAuth = to.matched.some((record) => record.meta.requiresAuth)
  const isGuestRoute = to.matched.some((record) => record.meta.guest)
  const isPublicRoute = to.matched.some((record) => record.meta.public)
  const isOnboardingRoute = to.matched.some((record) => record.meta.onboarding)
  const isAdminRoute = to.matched.some((record) => record.meta.admin)

  // Public routes are always accessible (e.g., reset-password)
  if (isPublicRoute) {
    next()
    return
  }

  // If user is not initialized, try to fetch
  if (!authStore.isInitialized) {
    await authStore.fetchUser()
  }

  // Not authenticated
  if (requiresAuth && !authStore.isAuthenticated) {
    next({ name: 'login', query: { redirect: to.fullPath } })
    return
  }

  // Already authenticated, trying to access guest route
  if (isGuestRoute && authStore.isAuthenticated) {
    next({ name: 'dashboard' })
    return
  }

  // Authenticated user - check workspaces
  if (authStore.isAuthenticated && !isGuestRoute) {
    // Fetch workspaces if not loaded
    if (workspaceStore.workspaces.length === 0 && !workspaceStore.isLoading) {
      await workspaceStore.fetchWorkspaces()
    }

    // No workspaces and not on onboarding - redirect to onboarding
    if (!workspaceStore.hasWorkspaces && !isOnboardingRoute) {
      next({ name: 'onboarding' })
      return
    }

    // Has workspaces but on onboarding - redirect to dashboard (unless creating new workspace)
    if (workspaceStore.hasWorkspaces && isOnboardingRoute && !to.query.create) {
      next({ name: 'dashboard' })
      return
    }

    // Admin route check
    if (isAdminRoute && !authStore.user?.is_admin) {
      next({ name: 'dashboard' })
      return
    }
  }

  next()
})

export default router
